import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, ShieldCheck, ArrowRight, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandLogo } from '@/components/BrandLogo';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const forgotPassword = async () => {
    const email = form.email.trim();
    if (!email) { toast.error('Önce e-posta adresinizi girin'); return; }
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Şifre sıfırlama bağlantısı e-postanıza gönderildi (kayıtlıysa).');
    } catch { toast.error('İşlem başarısız'); }
  };

  useEffect(() => {
    if (!loading && user && user.role === 'admin') navigate('/admin');
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) { toast.error('E-posta ve şifre gerekli'); return; }    setBusy(true);
    try {
      const res = await api.post('/auth/login', { email: form.email.trim(), password: form.password });
      if (res.data.user.role !== 'admin') {
        toast.error('Bu alan yalnızca yöneticiler içindir');
        setBusy(false);
        return;
      }
      setUser(res.data.user);
      navigate('/admin');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'E-posta veya şifre hatalı');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-page flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="absolute top-4 right-4"><ThemeToggle /></div>
      <div className="w-full max-w-sm fade-up">
        <div className="text-center mb-8">
          <BrandLogo className="text-ink mb-3 mx-auto block" />
          <div className="w-12 h-12 mx-auto rounded-full border border-line flex items-center justify-center mb-4">
            <ShieldCheck strokeWidth={1.5} className="w-6 h-6 text-gold" />
          </div>
          <h2 className="font-serif text-3xl text-ink">Yönetici Girişi</h2>
          <p className="text-ink/60 text-sm mt-2">Program yönetim paneline erişim</p>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="admin-login-form">
          <Row icon={Mail} testid="admin-email" type="email" placeholder="E-posta" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Row icon={Lock} testid="admin-password" type="password" placeholder="Şifre" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full flex items-center justify-center gap-2 bg-gold text-cream py-3.5 rounded-xl font-semibold transition-transform active:scale-95 disabled:opacity-50">
            {busy ? 'Lütfen bekleyin...' : 'Giriş Yap'} {!busy && <ArrowRight strokeWidth={1.5} className="w-4 h-4" />}
          </button>
        </form>

        <button data-testid="admin-forgot-password-link" onClick={forgotPassword}
          className="w-full mt-3 text-xs text-ink-faint hover:text-ink">
          Şifremi unuttum
        </button>

        <button data-testid="admin-back-link" onClick={() => navigate('/')}
          className="w-full mt-4 flex items-center justify-center gap-1.5 text-xs text-ink-faint hover:text-ink">
          <ArrowLeft strokeWidth={1.5} className="w-3.5 h-3.5" /> Mimar girişine dön
        </button>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, testid, type = 'text', placeholder, value, onChange }) => (
  <div className="relative">
    <Icon strokeWidth={1.5} className="w-4 h-4 text-ink-faint absolute left-3.5 top-1/2 -translate-y-1/2" />
    <input data-testid={testid} type={type} placeholder={placeholder} value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-ground border border-line rounded-xl py-3 pl-10 pr-3.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors" />
  </div>
);
